import {
    CdkDragDrop,
    moveItemInArray,
    transferArrayItem,
} from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Inject, Input, OnChanges, Optional, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SortDirection } from '@angular/material/sort';

export type ColumnType = {
    id: string;
    name: string;
    sort?: SortDirection;
    hidden?: boolean;
    index?: number;
};

@Component({
    selector: 'app-table-settings',
    styleUrls: ['./table-settings.component.scss'],
    templateUrl: './table-settings.component.html',
})
export class TableSettingsComponent implements OnChanges {
    // For AppDialogWrapperService pattern
    @Output() dialogClosed = new EventEmitter<ColumnType[]>();
    @Output() cancelDialog = new EventEmitter<void>();

    @Input() data: ColumnType[];

    columns: ColumnType[] = [];
    isSaveButtonDisabled = false;
    hasSelectAllColumn = false;

    // For backwards compatibility with direct MatDialog usage
    @Optional() private dialogRef?: MatDialogRef<TableSettingsComponent>;

    constructor(
        @Optional() dialogRef?: MatDialogRef<TableSettingsComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) injectedData?: ColumnType[]
    ) {
        // Backwards compatibility: support both injection patterns
        if (dialogRef) {
            this.dialogRef = dialogRef;
        }
        
        // Support both @Input and @Inject patterns
        const data = injectedData || this.data || [];
        this.columns = [...data].filter((c) => c.id != 'selectAll');
        this.hasSelectAllColumn = this.columns.length == data.length ? false : true;
    }

    ngOnChanges() {
        if (this.data) {
            this.columns = [...this.data].filter((c) => c.id != 'selectAll');
            this.hasSelectAllColumn = this.columns.length == this.data.length ? false : true;
        }
    }

    drop(event: CdkDragDrop<string[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        }
    }

    onSortChange(column: ColumnType) {
        if (!column.sort) {
            column.sort = 'asc';
            this.columns = this.columns.map((vc) =>
                vc.id === column.id
                    ? column
                    : {
                          hidden: vc.hidden,
                          id: vc.id,
                          name: vc.name,
                      }
            );
        } else if (column.sort === 'asc') {
            column.sort = 'desc';
        } else if (column.sort === 'desc') {
            column.sort = 'asc';
        }
    }

    toggleColumnVisible(column: ColumnType) {
        this.columns = this.columns.map((c) =>
            c.id === column.id
                ? {
                      ...column,
                      hidden: !column.hidden,
                  }
                : c
        );
        this.isSaveButtonDisabled = !this.hasAtLeastOneColumnVisible();
    }

    onSave() {
        if(this.hasSelectAllColumn){
            this.columns = [{
                id: 'selectAll',
                name: 'Select All',
            },...this.columns]
        }
        if (this.dialogRef) {
            // Direct MatDialog usage
            this.dialogRef.close(this.columns);
        } else {
            // AppDialogWrapperService usage
            this.dialogClosed.emit(this.columns);
        }
    }

    onCancel() {
        if (this.dialogRef) {
            // Direct MatDialog usage
            this.dialogRef.close();
        } else {
            // AppDialogWrapperService usage
            this.cancelDialog.emit();
        }
    }
    hasAtLeastOneColumnVisible() {
        return this.columns.filter((c) => !c.hidden).length > 0;
    }
}
